import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, del, get, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, ConfirmDialog, Empty, Failure, Field, Loading, Notice } from '../components/ui.js';
import { formatBytes, formatDateTime } from '../lib/format.js';

interface Release {
  id: string;
  version: string;
  board_profile: string;
  sha256: string;
  bytes: number;
  notes: string | null;
  created_at: string;
  devices_on_version: number;
}

interface FirmwareList {
  releases: Release[];
  devices: Array<{ id: string; name: string; school_name: string; firmware_version: string | null }>;
  max_bytes: number;
}

/*
 * Remote firmware updates for bell devices. A release is the compiled sketch;
 * each device downloads it over its own authenticated HTTPS channel, verifies the
 * SHA-256, installs it in the spare slot and reboots. A build that cannot reach
 * the platform afterwards rolls back to the previous one.
 */
export function FirmwarePage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  const [rollout, setRollout] = useState<Release | null>(null);
  const [removing, setRemoving] = useState<Release | null>(null);

  const list = useQuery({
    queryKey: ['firmware'],
    queryFn: () => get<FirmwareList>('/firmware'),
    refetchInterval: 10_000,
  });

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['firmware'] });

  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      // Fields first: the server reads them before the file stream.
      form.append('version', version.trim());
      if (notes.trim()) form.append('notes', notes.trim());
      form.append('file', file!, file!.name);
      return api<{ version: string }>('/firmware', { method: 'POST', body: form });
    },
    onSuccess: (r) => {
      setMessage({ tone: 'info', text: `رُفع الإصدار ${r.version}. أرسله إلى الأجهزة من الجدول أدناه.` });
      setVersion('');
      setNotes('');
      setFile(null);
      refresh();
    },
    onError: (e: Error) => setMessage({ tone: 'danger', text: e.message }),
  });

  const send = useMutation({
    mutationFn: (r: Release) => post<{ sent: number; already_on_version: number; note: string }>(`/firmware/${r.id}/rollout`, {}),
    onSuccess: (res) => {
      setRollout(null);
      setMessage({
        tone: 'info',
        text: `أُرسل التحديث إلى ${res.sent} جهاز (${res.already_on_version} عليه هذا الإصدار مسبقًا). ${res.note}`,
      });
      refresh();
    },
    onError: (e: Error) => {
      setRollout(null);
      setMessage({ tone: 'danger', text: e.message });
    },
  });

  const remove = useMutation({
    mutationFn: (r: Release) => del(`/firmware/${r.id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (e: Error) => {
      setRemoving(null);
      setMessage({ tone: 'danger', text: e.message });
    },
  });

  if (list.isLoading) return <Loading />;
  if (list.error) return <Failure error={list.error} onRetry={() => void list.refetch()} />;
  const data = list.data!;
  const manage = can('firmware.manage');
  const tooBig = file !== null && file.size > data.max_bytes;

  return (
    <>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      {manage ? (
        <Card title="رفع إصدار جديد" subtitle="لأجهزة الجرس الرقمي">
          <Notice tone="info">
            في Arduino IDE: <b>Sketch ← Export Compiled Binary</b>، ثم ارفع الملف المنتهي بـ <span className="mono">.ino.bin</span>{' '}
            (وليس <span className="mono">merged</span> أو <span className="mono">bootloader</span>). غيّر
            <span className="mono"> FIRMWARE_VERSION </span> في الكود قبل التصدير ليظهر الإصدار الجديد. ارفع النسخة
            المناسبة للعتاد: النسخة 2 لوحدة MP3، والنسخة 3 لبطاقة SD مع I2S.
          </Notice>
          <div className="grid cols-3">
            <Field label="رقم الإصدار" hint="مطابق لـ FIRMWARE_VERSION، مثل bell-2.1.0">
              <input className="mono" value={version} onChange={(e) => setVersion(e.target.value)} />
            </Field>
            <Field label="ملاحظات (اختياري)">
              <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
            </Field>
            <Field
              label="ملف البرنامج (.bin)"
              error={tooBig ? `أكبر من المسموح (${formatBytes(data.max_bytes)})` : undefined}
            >
              <input type="file" accept=".bin" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </Field>
          </div>
          <div className="row end">
            <button
              className="primary"
              disabled={!file || !version.trim() || tooBig || upload.isPending}
              onClick={() => upload.mutate()}
            >
              {upload.isPending ? 'جارٍ الرفع…' : 'رفع الإصدار'}
            </button>
          </div>
        </Card>
      ) : null}

      <Card title="الإصدارات">
        {data.releases.length === 0 ? (
          <Empty title="لم يُرفع أي إصدار بعد" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الإصدار</th>
                  <th>الحجم</th>
                  <th>تاريخ الرفع</th>
                  <th>الأجهزة عليه</th>
                  <th>ملاحظات</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.releases.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.version}</td>
                    <td style={{ fontSize: 12.5 }}>{formatBytes(r.bytes)}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(r.created_at)}</td>
                    <td className="num">
                      {r.devices_on_version} / {data.devices.length}
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{r.notes ?? '—'}</td>
                    <td className="actions">
                      {manage ? (
                        <>
                          <button
                            className="primary"
                            disabled={r.devices_on_version === data.devices.length}
                            onClick={() => setRollout(r)}
                          >
                            تحديث كل الأجهزة
                          </button>{' '}
                          <button className="ghost" onClick={() => setRemoving(r)}>
                            حذف
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="أجهزة الجرس" subtitle="لتحديث جهاز واحد افتح صفحته">
        {data.devices.length === 0 ? (
          <Empty title="لا توجد أجهزة جرس مفعّلة" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الجهاز</th>
                  <th>المدرسة</th>
                  <th>الإصدار الحالي</th>
                </tr>
              </thead>
              <tbody>
                {data.devices.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`/devices/${d.id}`}>{d.name}</Link>
                    </td>
                    <td>{d.school_name}</td>
                    <td>
                      <Badge tone="muted">
                        <span className="mono">{d.firmware_version ?? '—'}</span>
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {rollout ? (
        <ConfirmDialog
          title={`تحديث الأجهزة إلى ${rollout.version}`}
          confirmLabel="إرسال التحديث"
          busy={send.isPending}
          message={
            <>
              يُرسل التحديث إلى كل أجهزة الجرس التي لا تعمل بهذا الإصدار. ينزّل كل جهاز الملف ويتحقق منه ثم يعيد
              التشغيل (نحو دقيقة، يتوقف الجرس خلالها). إن لم يتصل الإصدار الجديد بالمنصة يعود الجهاز تلقائيًا للإصدار
              السابق. يُفضّل التحديث خارج وقت الدوام.
            </>
          }
          onCancel={() => setRollout(null)}
          onConfirm={() => send.mutate(rollout)}
        />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={`حذف الإصدار ${removing.version}`}
          danger
          busy={remove.isPending}
          confirmLabel="حذف"
          message="يُحذف الإصدار من القائمة. الأجهزة التي ثبّتته تستمر بالعمل عليه."
          onCancel={() => setRemoving(null)}
          onConfirm={() => remove.mutate(removing)}
        />
      ) : null}
    </>
  );
}
